import React, { useState, useMemo, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, Row, Col, Badge, ListGroup, Nav, Spinner } from 'react-bootstrap';
import axios from 'axios'; // มั่นใจว่าโปรเจกต์คุณมีติดตั้งไว้อยู่แล้วนะคร้าบ
import { toast } from 'react-toastify';
import { fetchDriverStatusByDate } from '../../../services/auth.ts';

const CarStatusCalendar = () => {
  const todayStr = new Date().toISOString().split('T')[0];
  // 1. เปลี่ยนจากใช้วันล็อกตายตัว เป็นการใช้ Dynamic Date (หรือวันปัจจุบัน)
  const [selectedDate, setSelectedDate] = useState(todayStr); // วันที่ปัจจุบัน
  const [activeSubTab, setActiveSubTab] = useState('active');

  // 2. State สำหรับเก็บข้อมูลจริงจาก API และสถานะการโหลด
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // สำหรับค้นหา

  // 3. ฟังก์ชันดึงข้อมูลจาก API เส้นใหม่ของคุณ
  const fetchDriverStatus = async (dateStr) => {
    setLoading(true);
    // 💡 ดักล้างเผื่อเจอกรณี String ขยะ หรือ object หลุดเข้ามา
    const cleanDateStr = typeof dateStr === 'string' ? dateStr.trim() : "";
    
    console.log("🚀 วันที่กำลังจะยิงไปหาหลังบ้านคือ:", cleanDateStr);

    try {
      const data = await fetchDriverStatusByDate(cleanDateStr);
      console.log("=== ข้อมูลจาก API หลังโยกไฟล์ ===", data);

      if (data && data.success) {
        setApiData(data.result || []);
      } else {
        setApiData([]);
        toast.error('ดึงข้อมูลไม่สำเร็จ');
      }
    } catch (error) {
      console.error('Error fetching driver status:', error);
      setApiData([]);
      toast.error('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
    } finally {
      setLoading(false);
    }
  };

  // 4. โหลดข้อมูลครั้งแรกเมื่อหน้าจอเปิดใช้งาน
  useEffect(() => {
    fetchDriverStatus(selectedDate);
  }, []);

  // 1. ปรับจุดแสดงบนปฏิทินให้เช็ก String วันที่ตรงๆ
  const events = useMemo(() => {
    if (!apiData || apiData.length === 0) return [];
    const datesWithJob = [
      ...new Set(apiData.map((item) => {
        // รองรับทั้งแบบ String ตรงๆ และแบบมี .$date
        const dateStr = typeof item.date_insert === 'string' ? item.date_insert : item.date_insert?.$date;
        return dateStr ? dateStr.split('T')[0] : null;
      }).filter(Boolean))
    ];
    return datesWithJob.map((date) => ({
      date: date,
      display: 'list-item',
      color: '#28a745'
    }));
  }, [apiData]);

  // 2. ปรับ Logic การแยกกลุ่ม และใส่ตัวกรองค้นหา (เวอร์ชันผสานฐานข้อมูลคนขับหลัก)
  const { activeCars, inactiveCars } = useMemo(() => {
    if (!apiData) return { activeCars: [], inactiveCars: [] };

    let activeResult = [];
    let inactiveResult = [];

    // แตกกลุ่มพนักงานตามผลลัพธ์ที่ Backend สรุปมาให้
    apiData.forEach((driver) => {
      if (driver.has_job) {
        // 🟢 กลุ่มที่มีงานวันนี้: คัดกรองเศษใบงานซ้ำ (ถ้ามี) ข้างในอาเรย์ plans ก่อนนำไปโชว์
        const uniquePlans = [];
        const seenPlanIds = new Set();
        
        driver.plans?.forEach((plan) => {
          if (plan.planid && !seenPlanIds.has(plan.planid)) {
            seenPlanIds.add(plan.planid);
            uniquePlans.push(plan);
          }
        });

        activeResult.push({
          ...driver,
          plans: uniquePlans
        });
      } else {
        // 💤 กลุ่มที่ไม่มีงานวันนี้เลย: จัดส่งไปอยู่กลุ่มไม่ได้วิ่งทันที
        inactiveResult.push(driver);
      }
    });

    // 🔍 ระบบช่องค้นหา Real-time
    if (searchTerm.trim() !== '') {
      const lowSearch = searchTerm.toLowerCase();

      // ค้นหาฝั่งวิ่งงาน (ค้นได้ด้วย ชื่อ, ทะเบียน, คลังสินค้า และ รหัสใบงานย่อย)
      activeResult = activeResult.filter(car => {
        const matchBasic = 
          car.NAME?.toLowerCase().includes(lowSearch) ||
          car.PLATE_NO?.toLowerCase().includes(lowSearch) ||
          car.SITE?.toLowerCase().includes(lowSearch);

        const matchPlanId = car.plans?.some(plan => 
          plan.planid?.toLowerCase().includes(lowSearch)
        );

        return matchBasic || matchPlanId;
      });

      // ค้นหาฝั่งไม่ได้วิ่ง (ค้นด้วย ข้อมูลพื้นฐาน)
      inactiveResult = inactiveResult.filter(car =>
        car.NAME?.toLowerCase().includes(lowSearch) ||
        car.PLATE_NO?.toLowerCase().includes(lowSearch) ||
        car.SITE?.toLowerCase().includes(lowSearch)
      );
    }

    return { activeCars: activeResult, inactiveCars: inactiveResult };
  }, [apiData, searchTerm]);

  // 7. เมื่อกดเลือกวันที่บนปฏิทิน -> ให้สั่งยิง API ดึงข้อมูลใหม่ของวันนั้นทันที
  const handleDateClick = (arg) => {
    setSelectedDate(arg.dateStr);
    fetchDriverStatus(arg.dateStr); // ยิง API ซ้ำด้วยวันที่ใหม่
  };

  const renderCarItem = (car) => {
    let driverImageUrl = 'https://via.placeholder.com/150';
    if (car.IMG_DRIVER) {
      driverImageUrl = car.IMG_DRIVER.startsWith('http')
        ? car.IMG_DRIVER
        : `https://api-passport.advanceagro.net/qrapi/driver/${car.IMG_DRIVER}`;
    }

    return (
      <ListGroup.Item
        key={car._id?.$oid || car.CAR_ID}
        className="p-3 mb-3 border border-light shadow-sm bg-white"
        style={{ borderRadius: '16px' }}
      >
        {/* 1. ส่วนหัวการ์ด: ข้อมูลคนขับและยานพาหนะ */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 border-bottom border-light">
          <div className="d-flex align-items-center flex-wrap gap-2">
            {/* รูปคนขับ */}
            <div className="position-relative me-2 mx-2">
              <img
                src={driverImageUrl}
                alt="driver"
                className="rounded-circle shadow-xs"
                style={{ width: '55px', height: '55px', objectFit: 'cover' }}
              />
            </div>
            <div>
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '1.05rem' }}>
                {car.NAME}
              </h6>
              <div className="text-muted small mt-1">
                <i className="fas fa-phone-alt text-success me-1"></i>{car.PHONE}
              </div>
            </div>
          </div>

          {/* ข้อมูลรถและประเภทรถ */}
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-end text-end">
            <div>
              <span className="badge bg-light text-dark border-0 px-2 py-1.5 fw-semibold" style={{ fontSize: '0.85rem', borderRadius: '6px' }}>
                <i className="fas fa-car text-primary me-1"></i>{car.PLATE_NO}
              </span>
              <div className="text-muted small mt-1" style={{ fontSize: '0.8rem' }}>
                <i className="fas fa-map-marker-alt text-danger me-1"></i> {car.SITE}
                <Badge
                  bg={activeSubTab === 'active' ? 'success' : 'secondary'}
                  className={`mx-2 px-2.5 py-2 fw-semibold ${activeSubTab === 'active' ? 'bg-opacity-10 text-success' : 'bg-opacity-10 text-secondary'}`}
                  style={{ borderRadius: '6px', fontSize: '0.8rem' }}
                >
                  {car.TYPE}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* 2. ส่วนแถบสรุปสั้นๆ (Sub-header) */}
        {activeSubTab === 'active' && (
          <div className="d-flex justify-content-between align-items-center mt-3 mb-2 px-1">
            <span className="fw-bold text-secondary small" style={{ letterSpacing: '0.5px', fontSize: '0.85rem' }}>
              <i className="fas fa-route text-primary me-2"></i>ลำดับใบงานวันนี้
            </span>
            <span className="badge bg-primary bg-opacity-10 text-primary fw-bold px-2 py-1" style={{ borderRadius: '6px', fontSize: '0.75rem' }}>
              {car.plans?.length || 0} ใบงาน
            </span>
          </div>
        )}

        {/* 3. ส่วนแสดงรายการใบงานย่อย (Timeline แบบแก้เพี้ยน + เรียงเวลาจากเช้าไปเย็น) */}
        {activeSubTab === 'active' && car.plans && (
          <div className="position-relative mt-2 ps-3 mx-4" style={{ borderLeft: '2px dashed #e9ecef' }}>
            {/* 🚀 ใช้ .slice().sort() เพื่อเรียงลำดับเวลาจากน้อยไปมาก (เวลาที่มาก่อนขึ้นก่อน) */}
            {car.plans
              .slice() // ใช้ slice เพื่อ copy อาเรย์ป้องกันไม่ให้เกิด mutation error ใน React
              .sort((a, b) => {
                const timeA = a.date_insert ? new Date(a.date_insert).getTime() : 0;
                const timeB = b.date_insert ? new Date(b.date_insert).getTime() : 0;
                return timeA - timeB; // น้อยไปมาก (เช่น 08:00 น. จะขึ้นก่อน 13:00 น.)
              })
              .map((plan, idx) => {
                const planTime = plan.date_insert
                  ? new Date(plan.date_insert).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
                  : '--:--';

                return (
                  <div key={idx} className="position-relative mb-2 pb-1">
                    {/* จุดวงกลมล็อกตำแหน่ง */}
                    <div
                      className="position-absolute bg-white border border-primary border-2 rounded-circle"
                      style={{ width: '10px', height: '10px', left: '-21px', top: '16px' }}
                    />

                    {/* ตัวกล่องใบงานย่อย */}
                    <div className="bg-light bg-opacity-50 px-3 py-2 rounded-3 border border-light transition-all">
                      <div className="row align-items-center g-2">
                        {/* ฝั่งซ้าย: เลขลำดับ + Plan ID */}
                        <div className="col-7 d-flex align-items-center gap-1.5">
                          <span className="text-muted fw-bold small me-1">
                            เลขใบงาน:
                          </span>
                          <span className="mx-2 fw-bold text-dark text-monospace" style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>
                            {plan.planid}
                          </span>
                        </div>

                        {/* ฝั่งขวา: เวลาวิ่งงาน */}
                        <div className="col-5 text-end d-flex align-items-center justify-content-end gap-1.5">
                          <span className="text-muted d-none d-sm-inline" style={{ fontSize: '0.8rem' }}>เวลากดรับงาน:</span>
                          <span className="mx-2 fw-bold text-primary" style={{ fontSize: '0.9rem' }}>{planTime} น.</span>
                          <i className="far fa-clock text-warning small"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </ListGroup.Item>
    );
  };

  // ... โค้ดส่วนหลัก (return บล็อกล่างสุด) คงเดิมทุกประการครับ ...

  return (
    <div className="p-1">
      {/* 🚀 เพิ่มผสานความกว้างให้เต็ม 12 ช่อง (Grid System) */}
      <Row className="g-4">
        {/* คอลัมน์ซ้าย: ปฏิทินขนาดเล็ก */}
        <Col xl={4} lg={5} md={12}>
          <Card className="border-0 shadow-sm rounded-4 h-100"> {/* 🚀 ใส่ h-100 เพื่อให้ความสูงยืดเสมอกับฝั่งขวา */}
            <Card.Body className="p-3">
              <div className="custom-mini-calendar">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  initialDate="2026-05-13"
                  locale="th"
                  height="auto"
                  events={events}
                  dateClick={handleDateClick}
                  headerToolbar={{ left: 'prev', center: 'title', right: 'next' }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* คอลัมน์ขวา: รายชื่อแยกแท็บ */}
        <Col xl={8} lg={7} md={12}>
          {/* 🚀 ปรับความสูงแบบพอดี และตัดขอบนุ่มนวลขึ้น */}
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden d-flex flex-column" style={{ height: '625px' }}>
            <Card.Header className="bg-white border-0 pt-3 px-4">
              <div className="mb-3">
                <h6 className="fw-bold text-primary mb-1" style={{ fontSize: '1.05rem' }}>
                  <i className="fas fa-calendar-day me-2"></i>
                  สถานะวันที่:{' '}
                  {new Date(selectedDate).toLocaleDateString('th-TH', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </h6>
              </div>

              {/* 🚀 3. เพิ่มกล่องค้นหาดีไซน์คลีนๆ ทันสมัย */}
              <div className="mb-3 position-relative">
                <input
                  type="text"
                  className="form-control bg-light border-0 ps-5 py-2.5"
                  style={{ borderRadius: '10px', fontSize: '0.9rem' }}
                  placeholder="ค้นหาชื่อคนขับ, ทะเบียนรถ, หรือเลขใบงาน"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} // พิมพ์ปุ๊บเก็บค่าเข้า State ปั๊บ
                />
                {searchTerm && (
                  <button
                    className="btn btn-sm position-absolute top-50 end-0 translate-middle-y pe-3 border-0 bg-transparent text-muted"
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="fas fa-times-circle"></i>
                  </button>
                )}
              </div>
              {/* ปรับ Nav Pills ให้ดูเต็มตึง สบายตา */}
              <Nav variant="pills" className="bg-light p-1 rounded-3" style={{ fontSize: '0.95rem' }}>
                <Nav.Item className="col-6 text-center">
                  <Nav.Link
                    active={activeSubTab === 'active'}
                    onClick={() => setActiveSubTab('active')}
                    className="border-0 py-2.5 fw-semibold"
                  >
                    กดรับงาน ({activeCars.length} ใบงาน)
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="col-6 text-center">
                  <Nav.Link
                    active={activeSubTab === 'inactive'}
                    onClick={() => setActiveSubTab('inactive')}
                    className="border-0 py-2.5 fw-semibold"
                  >
                    ไม่มีกดรับงาน ({inactiveCars.length} คน)
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>

            {/* 🚀 ปรับช่องไฟ p-3 และเอา mx-4 ออก เพื่อให้สมมาตรเข้ากันกับ Header ด้านบน */}
            <Card.Body className="p-3 position-relative flex-grow-1 overflow-hidden">
              {/* โชว์ปุ่มหมุนๆ ตอนกำลังกวาดข้อมูลจาก API */}
              {loading ? (
                <div className="d-flex align-items-center justify-content-center h-100 py-5">
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2 small fw-semibold">กำลังดึงข้อมูลงาน...</p>
                  </div>
                </div>
              ) : (
                /* 🚀 ปรับขยายความสูงของพื้นที่ Scrollbar ด้านในให้คุ้มกับความสูงการ์ด (ใช้ 100% แทนล็อกเลข) */
                <div style={{ height: '100%', overflowY: 'auto', paddingRight: '4px' }} className="scroll-area">
                  <ListGroup variant="flush" key={activeSubTab}>
                    {(activeSubTab === 'active' ? activeCars : inactiveCars).length > 0 ? (
                      (activeSubTab === 'active' ? activeCars : inactiveCars).map(renderCarItem)
                    ) : (
                      <div className="text-center py-5 my-5">
                        <i className="fas fa-car-side fa-3x text-light mb-3 opacity-50"></i>
                        <p className="text-muted fw-semibold">ไม่พบข้อมูลพนักงานในกลุ่มนี้</p>
                      </div>
                    )}
                  </ListGroup>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};


export default CarStatusCalendar;
